import styled from 'styled-components';
import Typography from '../../../../../components/typography/Typography';
import { OrderDTO, OrderStatus } from '../../../../../api/api';
import { formatDateString, formatTimeAndDateString } from '../../../../../utils/dateHandling';
import { getInitialsFromEmail } from '../../../../../utils/initialsUtils';

import { useReceiptList } from './useReceiptList';
import Table from '../../../../../blocks/table/TableClient';
import { ErrorIcon } from '../../../../../assets/icons/ErrorIcon';
import n1LightTheme from '../../../../../theme/light-theme';

interface Props {
  order: OrderDTO;
}

const OrderReceiptContent = (props: Props) => {
  const { order } = props;

  const { tableInstance } = useReceiptList(order.items ?? []);

  return (
    <ReceiptContainer>
      {!order.isFulfilled && (
        <AlertContainer>
          <ErrorHeader>
            <ErrorIcon size="22px" color={n1LightTheme.palette.functions.error.primary} />
            <Typography margin="0 0 0px 8px" color="error" variant="h5" fontWeight="bold">
              En eller flere varer kan ikke leveres
            </Typography>
          </ErrorHeader>
          <Typography color="error" variant="h6">
            Venligst kontakt Supply Chain (Lager & Logistik) for at aftale alternative muligheder.
          </Typography>
          <Typography color="error" variant="h6">
            De kan træffes på tlf: 7518 7744. (mandag til torsdag fra kl. 08 - 15 og fredag fra kl. 08 - 12).
          </Typography>
        </AlertContainer>
      )}
      <OrderDetails>
        {order.created && new Date(order.created).getFullYear() > 1 && (
          <Detail>
            <Typography variant="h5">
              Oprettet: {''}
              <b>
                {formatTimeAndDateString(order.created)} ({getInitialsFromEmail(order.createdByEmail ?? '')})
              </b>
            </Typography>
          </Detail>
        )}
        <Detail>
          <Typography variant="h5">
            Bestilt til: {''}
            <b>
              {order.toWarehouseName} ({order.toWarehouseId})
            </b>
          </Typography>
        </Detail>
      </OrderDetails>

      <TableContainer>
        <Table
          loading={false}
          tableInstance={tableInstance}
          noPadding
          noDataText={
            order.orderStatus === OrderStatus.Created
              ? 'Bestilling er sendt til D365. Du vil kunne se dine varer her, når bestillingen er gået igennem'
              : ''
          }
        />
      </TableContainer>

      <FooterContainer>
        <FooterDetails>
          <Detail>
            <Typography>Forventet leveringsdato</Typography>
            <Typography fontWeight="bold">
              {order.isFulfilled ? formatDateString(order.expectedReceiptDate ?? '') : 'Ukendt'}
            </Typography>
          </Detail>
          <Detail>
            <Typography color="greyedOut">Ønsket leveringsdato</Typography>
            <Typography fontWeight="bold" color="greyedOut">
              {formatDateString(order.requestedReceiptDate ?? '')}
            </Typography>
          </Detail>
          <Detail>
            <Typography>Besked på pakken</Typography>
            <Typography fontWeight="bold">{order.description}</Typography>
          </Detail>
        </FooterDetails>
      </FooterContainer>
    </ReceiptContainer>
  );
};

const ReceiptContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: calc(100% - 60px);
  align-items: flex-start;
  padding: 0 ${(props) => props.theme.spacing(7.5)} ${(props) => props.theme.spacing(7.5)}
    ${(props) => props.theme.spacing(7.5)};
  background-color: ${(props) => props.theme.palette.background.primary};
  border-radius: ${(props) => props.theme.spacing(3)};
`;

const OrderDetails = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin-top: ${(props) => props.theme.spacing(5)};
`;

const Detail = styled.div`
  flex-direction: column;
  margin-right: ${(props) => props.theme.spacing(5)};
  width: 50%;
  text-align: left;
  :last-child {
    margin-right: 0;
    text-align: right;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  width: 100%;
  margin-top: ${(props) => props.theme.spacing(7)};
  overflow-y: auto;
`;

const FooterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const FooterDetails = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-bottom: ${(props) => props.theme.spacing(3)};
`;

const AlertContainer = styled.div`
  padding-top: ${(props) => props.theme.spacing(3)};
`;

const ErrorHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
`;

export default OrderReceiptContent;
